/**
 * Shared error-monitoring policy.
 *
 * This module is the source of truth for every project's Bugsnag
 * configuration. It imports nothing, so it is safe in React Native, in the
 * browser, and in Node.
 *
 * `api` cannot consume this package (it has no `packages` submodule and no
 * @point2-inc dependency), so it keeps a copy in
 * `libs/observability/src/lib/policy.ts`. Change both together.
 */

/** The four deployment tiers, used verbatim as the Bugsnag `releaseStage`. */
export const APP_TIERS = ['local', 'development', 'staging', 'production'] as const;

export type AppTier = (typeof APP_TIERS)[number];

/**
 * Every tier transmits.
 *
 * This used to be `['staging', 'production']`, on the reasoning that local and
 * development noise would eat the monthly event quota. That reasoning cost us
 * the thing monitoring is for: a bug that only reproduces on a developer's
 * machine or on the development tier produced no event, so nobody could tell a
 * broken integration from a quiet one, and "is Bugsnag even wired up?" could
 * only be answered by deploying. The tiers are distinguished by `releaseStage`
 * on the dashboard instead — filter there, not at the source.
 *
 * Turning reporting off is now an explicit, per-app decision, not a property of
 * the tier: see the kill switch each app documents in its `.env.example`
 * (`BUGSNAG_ENABLED` on the API, `NEXT_PUBLIC_BUGSNAG_ENABLED` on Admin and
 * Web, `EXPO_PUBLIC_BUGSNAG_ENABLED` on the two mobile apps).
 */
export const ENABLED_RELEASE_STAGES: readonly AppTier[] = [...APP_TIERS];

export const isAppTier = (value: unknown): value is AppTier =>
  typeof value === 'string' && (APP_TIERS as readonly string[]).includes(value);

/**
 * A Bugsnag project API key is 32 hexadecimal characters.
 *
 * Worth checking rather than trusting: the failure mode for a malformed key is
 * that the SDK starts, accepts events, posts them, and the dashboard silently
 * drops them — indistinguishable from an app that never crashes.
 */
export const BUGSNAG_API_KEY_PATTERN = /^[0-9a-f]{32}$/i;

export type MonitoringKeyProblem = 'missing' | 'malformed';

export interface ResolvedMonitoringKey {
  /** The trimmed key, or undefined when it cannot be used. */
  key?: string;
  problem?: MonitoringKeyProblem;
  /** One line, safe to log. Never contains the key itself. */
  message?: string;
}

/**
 * Normalise one raw environment value into a usable key or a named problem.
 *
 * Trimming is not cosmetic. A key pasted into an EAS or Vercel dashboard field
 * routinely carries a trailing space or newline, and an untrimmed key is a
 * malformed key — see above for how quietly that fails.
 *
 * `missing` covers both absent and blank, because `KEY=` in a dotenv file is a
 * blank string, not an absent variable, and both mean "not configured".
 */
export const resolveMonitoringKey = (raw: unknown): ResolvedMonitoringKey => {
  const trimmed = typeof raw === 'string' ? raw.trim() : '';

  if (!trimmed) {
    return { problem: 'missing', message: 'Bugsnag API key is not set.' };
  }
  if (!BUGSNAG_API_KEY_PATTERN.test(trimmed)) {
    return {
      problem: 'malformed',
      message:
        `Bugsnag API key is malformed (expected 32 hex characters, got ${trimmed.length} ` +
        `characters). Events would be accepted locally and dropped by the dashboard.`,
    };
  }
  return { key: trimmed };
};

/**
 * Keys whose values must never leave the device. Matched by Bugsnag against
 * metadata, breadcrumbs and request payloads before an event is sent.
 *
 * Union of the lists previously maintained separately in
 * `customer/src/libs/bugsnag.ts` and `operator/src/libs/bugsnag.ts`.
 *
 * Note what this does NOT cover: a secret that is part of a *value* rather than
 * the name of a key. A redacted key named `token` does nothing about
 * `https://point2.ng/trip/share/<token>` sitting in `event.context`. Use
 * `sanitizeUrl` for those.
 */
export const MONITORING_REDACTED_KEYS: readonly (string | RegExp)[] = [
  /password/i,
  /token/i,
  /secret/i,
  /authorization/i,
  /refresh_?token/i,
  /access_?token/i,
  /identityToken/i,
  /authorizationCode/i,
  /api_?key/i,
  /bvn/i,
  /\bnin\b/i,
  /otp/i,
  /\bpin\b/i,
  /cvv/i,
  /card/i,
  /account_?number/i,
  /\bcredential/i,
  /signature/i,
];

/**
 * URL path segments that are followed by a secret rather than an identifier.
 *
 * `/trip/share/<token>` is the one that matters today: the token is the entire
 * authentication for a shared trip, so a URL in a crash report is a live
 * credential for someone's journey. `/verify/` and `/reset/` follow the same
 * shape.
 */
// `[^/?#\s]+` and not `[^/?#]+`: a URL inside an error message is followed by
// ordinary prose, and the greedy version swallowed it — "GET /trip/share/tok
// failed" redacted down to "GET /trip/share/[redacted]", taking the reason for
// the failure with it.
const SECRET_PATH_SEGMENTS = /\/(share|verify|reset|invite|confirm|magic)\/[^/?#\s]+/gi;

/** Query parameters whose value is a secret. */
const SECRET_QUERY_PARAMS =
  /([?&](?:token|access_token|refresh_token|code|key|api_?key|secret|signature|otp|pin)=)[^&#]*/gi;

/**
 * Strips secrets that live inside a URL's own text.
 *
 * Kept deliberately narrow: the path shape and the query key are preserved, so
 * `/trip/share/[redacted]` still tells you which page broke and which route to
 * reproduce. Redaction that removes the diagnosis along with the secret just
 * moves the outage somewhere else.
 */
export const sanitizeUrl = (value: string): string => {
  if (!value) return value;
  return value
    .replace(SECRET_PATH_SEGMENTS, (_match, segment: string) => `/${segment}/[redacted]`)
    .replace(SECRET_QUERY_PARAMS, (_match, prefix: string) => `${prefix}[redacted]`);
};

/**
 * Applies `sanitizeUrl` to every URL-ish string it can find in a value,
 * recursively. Bounded in depth so a cyclic or enormous object cannot hang the
 * error path — reporting must never be the thing that breaks.
 */
export const sanitizeUrlsDeep = <T>(value: T, depth = 0): T => {
  if (depth > 6) return value;
  if (typeof value === 'string') {
    return (value.includes('://') || value.startsWith('/') ? sanitizeUrl(value) : value) as T;
  }
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeUrlsDeep(item, depth + 1)) as T;
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
      out[key] = sanitizeUrlsDeep(item, depth + 1);
    }
    return out as T;
  }
  return value;
};

/**
 * Expected, self-recovering failures that carry no debugging value.
 *
 * Narrowed deliberately. The previous list matched bare `\btimeout\b`,
 * `\baborted\b` and `cancell?ed` anywhere in an error's name, message or code,
 * which silently discarded a whole class of real incidents: a database
 * statement timeout, a payment the provider cancelled, a settlement
 * transaction that aborted. A gateway outage looked exactly like a user
 * walking into a lift.
 *
 * What stays is transport noise whose wording is specific enough to be
 * unambiguous — an axios/fetch abort, a React Native network blip, an expired
 * session. Anything else is now reported, and filtered on the dashboard if it
 * turns out to be noise after all.
 */
export const NON_ACTIONABLE_ERROR_PATTERNS: readonly RegExp[] = [
  /\bnetwork error\b/i,
  /network request failed/i,
  /timeout of \d+ms exceeded/i,
  /\beconnaborted\b/i,
  /\berr_network\b/i,
  /\berr_canceled\b/i,
  /\berr_internet_disconnected\b/i,
  /request aborted/i,
  /\baborterror\b/i,
  /the operation was aborted/i,
  /request failed with status code 401/i,
  /session expired/i,
  /load failed/i,
];

/**
 * True when `haystack` — a lowercased join of error name, message, code and
 * Bugsnag errorClass — matches anything not worth reporting.
 */
export const isNonActionableError = (haystack: string): boolean =>
  NON_ACTIONABLE_ERROR_PATTERNS.some((pattern) => pattern.test(haystack));
